import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Chip,
  TableSortLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
  InputAdornment,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import CachedIcon from '@mui/icons-material/Cached';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';

import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { Flip } from 'gsap/Flip';
import { universalModalPaperProps, universalModalBackdropProps } from '../styles/modalStyles';
import { AttendanceStudent, AttendanceAwardCheckResult, AttendanceAwardCheckResponse } from '../types';

// Register GSAP plugins
gsap.registerPlugin(Draggable, Flip);

// Using regular HTML date input instead of MUI DatePicker to avoid dependency issues

// API base URL
const API_BASE_URL = '/api/attendance';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'lss_attendance_cache_';

// Available columns for display
const AVAILABLE_COLUMNS = [
  { key: 'Student Name', label: 'Student Name', defaultVisible: true },
  { key: 'Course Type', label: 'Course Type', defaultVisible: true },
  { key: 'Date of Birth', label: 'Date of Birth', defaultVisible: true },
  { key: 'LSS ID', label: 'LSS ID', defaultVisible: true },
  { key: 'Add-Ons', label: 'Add-Ons', defaultVisible: true },
  { key: 'Prerequisites', label: 'Prerequisites Status', defaultVisible: false },
  { key: 'Email', label: 'Email', defaultVisible: false },
  { key: 'Date Scheduled', label: 'Date Scheduled', defaultVisible: false },
  { key: 'Location', label: 'Location', defaultVisible: false },
  { key: 'Course Start Date', label: 'Course Start Date', defaultVisible: false },
  { key: 'Course Schedule', label: 'Course Schedule', defaultVisible: false },
  { key: 'Friend Group', label: 'Friend Group', defaultVisible: false },
  { key: 'Status', label: 'Status', defaultVisible: false },
  { key: 'Notes', label: 'Notes', defaultVisible: false },
  { key: 'Previous Appointment', label: 'Previous Appointment', defaultVisible: false },
  { key: 'Alternate Phone', label: 'Alternate Phone', defaultVisible: false },
  { key: 'Alternate Email', label: 'Alternate Email', defaultVisible: false },
  { key: 'Label', label: 'Label', defaultVisible: false },
  { key: 'Emergency Contact', label: 'Emergency Contact', defaultVisible: false },
  { key: 'Emergency Number', label: 'Emergency Number', defaultVisible: false }
] as const;

interface Appointment {
  'Student Name': string;
  'Email': string;
  'Course Type': string;
  'Add-Ons': string;
  'Date Scheduled': string;
  'Location': string;
  'Course Schedule': string;
  'Course Start Date': string;
  'Date of Birth': string;
  'LSS ID': string;
  'Friend Group': string;
  'Status': string;
  'Notes': string;
  'Previous Appointment': string;
  'Alternate Phone': string;
  'Alternate Email': string;
  'Label': string;
  'Emergency Contact': string;
  'Emergency Number': string;
  '_row_number': number;
  [key: string]: string | number;
}

interface UniqueValues {
  course_types: string[];
  locations: string[];
}

interface Filters {
  courseTypes: string[];
  location: string;
  startDate: Date | null;
}

interface DataSourceInfo {
  current_default: string;
  available_sources: string[];
  google_sheets_configured: boolean;
  acuity_configured: boolean;
}

interface CachedData {
  data: UniqueValues;
  timestamp: number;
  dataSource: string;
}

interface CacheStatus {
  isUsingCache: boolean;
  isRefreshing: boolean;
  lastRefresh: Date | null;
  cacheAge: number; // in minutes
}

interface AwardCheckStatus {
  isChecking: boolean;
  results: AttendanceAwardCheckResult[];
  lastCheck: Date | null;
  error: string | null;
}

interface StudentSearchResult {
  profile: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    notes?: string;
  };
  appointments: any[];
  appointment_count: number;
}

interface StudentSearchState {
  isSearching: boolean;
  searchTerm: string;
  results: StudentSearchResult[];
  selectedStudent: StudentSearchResult | null;
  showModal: boolean;
  error: string | null;
}

interface AppointmentDetails {
  'Student Name': string;
  'Email': string;
  'Course Type': string;
  'Date of Birth': string;
  'LSS ID': string;
  'Friend Group': string;
  'Course Start Date': string;
  'Course Schedule': string;
  'Add-Ons': string;
  'Location': string;
  'Physical Location'?: string;
  'Date Scheduled': string;
  'Unique ID': string;
  appointment_details: {
    appointment_id: number;
    calendar_id: number;
    appointment_type_id: number;
    duration: number;
    price: string;
    paid: string;
    amount_paid: string;
    certificate: string;
    confirmation_page: string;
    forms_text: string;
    notes: string;
    timezone: string;
    created_date: string;
    last_updated: string;
    canceled: boolean;
    package_id: number | null;
    addon_ids: number[] | null;
    labels: any[] | null;
  };
  structured_form_data: { [formName: string]: { [fieldName: string]: string } } | null;
  raw_appointment: any;
}

interface AppointmentDetailsState {
  isLoading: boolean;
  selectedAppointment: AppointmentDetails | null;
  showModal: boolean;
  error: string | null;
}

// Helper function to check if a friend group value is valid (not just "No", "N/A", etc.)
const isValidFriendGroup = (friendGroup: string): boolean => {
  if (!friendGroup || !friendGroup.trim()) return false;
  const normalized = friendGroup.toLowerCase().trim();
  const invalidValues = ['no', 'n/a', 'none', 'na', 'not applicable', 'nil'];
  return !invalidValues.includes(normalized);
};

// Helper function to check if a student should show the friend group icon
const shouldShowFriendGroupIcon = (studentName: string, studentFriendGroup: string, allAppointments: Appointment[]): boolean => {
  // Case 1: Student has a valid friend group (existing logic)
  if (isValidFriendGroup(studentFriendGroup)) {
    return true;
  }
  
  // Case 2: Student is mentioned by someone else as their friend group
  const isMentionedByOthers = allAppointments.some(appointment => 
    appointment['Student Name'] !== studentName && 
    isValidFriendGroup(appointment['Friend Group']) &&
    appointment['Friend Group'] === studentName
  );
  
  return isMentionedByOthers;
};

// Helper function to get friend group tooltip text
const getFriendGroupTooltip = (studentName: string, studentFriendGroup: string, allAppointments: Appointment[]): string => {
  // Case 1: Student has a valid friend group
  if (isValidFriendGroup(studentFriendGroup)) {
    return `Signed up with: ${studentFriendGroup}`;
  }
  
  // Case 2: Student is mentioned by others
  const mentionedBy = allAppointments
    .filter(appointment => 
      appointment['Student Name'] !== studentName && 
      isValidFriendGroup(appointment['Friend Group']) &&
      appointment['Friend Group'] === studentName
    )
    .map(appointment => appointment['Student Name']);
  
  if (mentionedBy.length > 0) {
    return `Friend group with: ${mentionedBy.join(', ')}`;
  }
  
  return 'Friend group';
};

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth: string): number => {
  if (!dateOfBirth) return 0;
  
  try {
    // Handle different date formats (YYYY/MM/DD, YYYY-MM-DD, etc.)
    const cleanDate = dateOfBirth.replace(/[-/]/g, '/');
    const birthDate = new Date(cleanDate);
    const today = new Date();
    
    if (isNaN(birthDate.getTime())) return 0;
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return Math.max(0, age);
  } catch (error) {
    console.warn(`Could not calculate age for date: ${dateOfBirth}`, error);
    return 0;
  }
};

// Helper function to get age group indicator for a classroom
const getAgeGroupIndicator = (classroom: (Appointment & { id: string })[]): { 
  label: string; 
  color: 'primary' | 'secondary' | 'success' | 'warning';
  tooltip: React.ReactNode;
} => {
  if (classroom.length === 0) {
    return { 
      label: 'No students', 
      color: 'secondary', 
      tooltip: (
        <Typography variant="body2">No students in this classroom</Typography>
      )
    };
  }

  // Calculate ages for all students with valid birth dates
  const ages = classroom
    .map(student => calculateAge(student['Date of Birth']))
    .filter(age => age > 0);
  
  if (ages.length === 0) {
    return { 
      label: 'Unknown ages', 
      color: 'secondary', 
      tooltip: (
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            Age Information
          </Typography>
          <Typography variant="body2">
            No valid birth dates found for age calculation
          </Typography>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
            {classroom.length} students total
          </Typography>
        </Box>
      )
    };
  }

  // Calculate average age
  const averageAge = Math.round(ages.reduce((sum, age) => sum + age, 0) / ages.length);
  
  // Count students above and below average
  const belowAverage = ages.filter(age => age < averageAge).length;
  const aboveAverage = ages.filter(age => age >= averageAge).length;
  const totalWithAges = ages.length;
  
  // Calculate percentages
  const belowPercentage = (belowAverage / totalWithAges) * 100;
  const abovePercentage = (aboveAverage / totalWithAges) * 100;
  
  // Determine label and color based on distribution
  let label: string;
  let color: 'primary' | 'secondary' | 'success' | 'warning';
  
  if (belowPercentage === 100) {
    label = `All under ${averageAge}`;
    color = 'success';
  } else if (abovePercentage === 100) {
    label = `All over ${averageAge}`;
    color = 'warning';
  } else if (belowPercentage >= 75) {
    label = `Mainly under ${averageAge}`;
    color = 'primary';
  } else if (abovePercentage >= 75) {
    label = `Mainly over ${averageAge}`;
    color = 'secondary';
  } else {
    label = `Mixed ages (avg ${averageAge})`;
    color = 'primary';
  }
  
  // Create detailed tooltip with better formatting
  const ageRange = ages.length > 0 ? `${Math.min(...ages)}-${Math.max(...ages)}` : 'Unknown';
  const tooltip = (
    <Box sx={{ p: 1 }}>
      
      
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Average:</strong> {averageAge} years old
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Range:</strong> {ageRange} years
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Students:</strong> {totalWithAges} of {classroom.length} with known ages
      </Typography>
      
      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', pt: 1 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Younger group:</strong> {belowAverage} students under {averageAge}
        </Typography>
        <Typography variant="body2">
          <strong>Older group:</strong> {aboveAverage} students {averageAge} and over
        </Typography>
      </Box>
      
      {totalWithAges < classroom.length && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
          {classroom.length - totalWithAges} students missing birth dates
        </Typography>
      )}
    </Box>
  );
  
  return { label, color, tooltip };
};

// Cache management functions
const getCacheKey = (dataSource: string) => `${CACHE_KEY_PREFIX}${dataSource}`;

const getCachedData = (dataSource: string): CachedData | null => {
  try {
    const cached = localStorage.getItem(getCacheKey(dataSource));
    if (!cached) return null;
    
    const parsedCache = JSON.parse(cached) as CachedData;
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - parsedCache.timestamp > CACHE_DURATION) {
      localStorage.removeItem(getCacheKey(dataSource));
      return null;
    }
    
    return parsedCache;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

const setCachedData = (dataSource: string, data: UniqueValues): void => {
  try {
    const cacheData: CachedData = {
      data,
      timestamp: Date.now(),
      dataSource
    };
    localStorage.setItem(getCacheKey(dataSource), JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

const clearCache = (dataSource?: string): void => {
  try {
    if (dataSource) {
      localStorage.removeItem(getCacheKey(dataSource));
    } else {
      // Clear all cache entries
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

const Attendance: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    courseTypes: [],
    location: '',
    startDate: null,
  });
  const [uniqueValues, setUniqueValues] = useState<UniqueValues>({
    course_types: [],
    locations: [],
  });
  const [availableCourseGroups, setAvailableCourseGroups] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState<string>('google_sheets');
  const [dataSourceInfo, setDataSourceInfo] = useState<DataSourceInfo | null>(null);
  const [sorting, setSorting] = useState<{ column: keyof Appointment | null; direction: 'asc' | 'desc' }>({ 
    column: null, 
    direction: 'asc' 
  });
  const [classroomView, setClassroomView] = useState<{
    enabled: boolean;
    numClassrooms: number;
    classrooms: (Appointment & { id: string })[][];
  }>({
    enabled: false,
    numClassrooms: 2,
    classrooms: []
  });

  // Cache status state
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    isUsingCache: false,
    isRefreshing: false,
    lastRefresh: null,
    cacheAge: 0
  });

  // Award checking status state
  const [awardCheckStatus, setAwardCheckStatus] = useState<AwardCheckStatus>({
    isChecking: false,
    results: [],
    lastCheck: null,
    error: null
  });

  // Bulk selection state
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [targetClassroom, setTargetClassroom] = useState<number | null>(null);
  
  // Split classroom modal state
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [numClassrooms, setNumClassrooms] = useState(2);
  const [sortByAge, setSortByAge] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key)
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>([]);
  
  // Move mode state
  const [moveMode, setMoveMode] = useState(false);

  // Student search state
  const [studentSearch, setStudentSearch] = useState<StudentSearchState>({
    isSearching: false,
    searchTerm: '',
    results: [],
    selectedStudent: null,
    showModal: false,
    error: null
  });

  // Appointment details state
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetailsState>({
    isLoading: false,
    selectedAppointment: null,
    showModal: false,
    error: null
  });

  // Refs for GSAP animations
  const classroomRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const totalStudentsRef = React.useRef<HTMLDivElement>(null);

  // Debounce timeout ref
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Fetch data source information
  const fetchDataSourceInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/data-source`);
      setDataSourceInfo(response.data);
      setDataSource(response.data.current_default);
    } catch (err) {
      console.error('Failed to fetch data source info:', err);
    }
  }, []);

  // Student search functions
  const searchStudents = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setStudentSearch(prev => ({ ...prev, results: [], error: null }));
      return;
    }

    setStudentSearch(prev => ({ ...prev, isSearching: true, error: null }));

    try {
      const response = await axios.get(`${API_BASE_URL}/search-students`, {
        params: { q: searchTerm, limit: 20 }
      });

      setStudentSearch(prev => ({
        ...prev,
        results: response.data.students,
        isSearching: false
      }));
    } catch (err: any) {
      console.error('Failed to search students:', err);
      setStudentSearch(prev => ({
        ...prev,
        isSearching: false,
        error: err.response?.data?.error || 'Failed to search students'
      }));
    }
  }, []);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setStudentSearch(prev => ({ ...prev, searchTerm: value }));
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      searchStudents(value);
    }, 500);
  };

  const handleSearchStudentSelect = (student: StudentSearchResult) => {
    setStudentSearch(prev => ({
      ...prev,
      selectedStudent: student,
      showModal: true
    }));
  };

  const handleCloseStudentModal = () => {
    setStudentSearch(prev => ({
      ...prev,
      selectedStudent: null,
      showModal: false
    }));
  };

  const formatAppointmentStatus = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return { text: 'Scheduled', color: 'success' as const };
      case 'canceled':
        return { text: 'Canceled', color: 'error' as const };
      case 'confirmed':
        return { text: 'Confirmed', color: 'primary' as const };
      default:
        return { text: status || 'Unknown', color: 'default' as const };
    }
  };

  // Appointment details functions
  const fetchAppointmentDetails = useCallback(async (appointmentId: number) => {
    setAppointmentDetails(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await axios.get(`${API_BASE_URL}/appointment-details/${appointmentId}`);

      setAppointmentDetails(prev => ({
        ...prev,
        selectedAppointment: response.data.appointment,
        showModal: true,
        isLoading: false
      }));
    } catch (err: any) {
      console.error('Failed to fetch appointment details:', err);
      setAppointmentDetails(prev => ({
        ...prev,
        isLoading: false,
        error: err.response?.data?.error || 'Failed to fetch appointment details'
      }));
    }
  }, []);

  const handleAppointmentClick = (appointmentId: number) => {
    fetchAppointmentDetails(appointmentId);
  };

  const handleCloseAppointmentModal = () => {
    setAppointmentDetails(prev => ({
      ...prev,
      selectedAppointment: null,
      showModal: false,
      error: null
    }));
  };

  // Enhanced fetch unique values with caching
  const fetchUniqueValues = useCallback(async (useCache: boolean = true, showRefreshIndicator: boolean = false) => {
    try {
      // Check cache first if enabled
      if (useCache) {
        const cachedData = getCachedData(dataSource);
        if (cachedData) {
          console.log('Using cached data for:', dataSource);
          setUniqueValues(cachedData.data);
          
          const cacheAge = Math.floor((Date.now() - cachedData.timestamp) / 60000);
          setCacheStatus({
            isUsingCache: true,
            isRefreshing: false,
            lastRefresh: new Date(cachedData.timestamp),
            cacheAge
          });
          

          
          return;
        }
      }
      
      // Set refreshing state
      if (showRefreshIndicator) {
        setCacheStatus(prev => ({ ...prev, isRefreshing: true }));
      }
      
      console.log('Fetching fresh data for:', dataSource);
      const response = await axios.get(`${API_BASE_URL}/unique-values?data_source=${dataSource}`);
      
      const newUniqueValues = {
        course_types: response.data?.course_types || [],
        locations: response.data?.locations || []
      };
      
      setUniqueValues(newUniqueValues);
      
      // Cache the data
      setCachedData(dataSource, newUniqueValues);
      
      // Update cache status
      setCacheStatus({
        isUsingCache: false,
        isRefreshing: false,
        lastRefresh: new Date(),
        cacheAge: 0
      });
      
      console.log('Fresh data fetched and cached for:', dataSource);
      
    } catch (err) {
      console.error('Failed to fetch unique values:', err);
      
      // If fresh fetch fails, try to use cached data as fallback
      if (!useCache) {
        const cachedData = getCachedData(dataSource);
        if (cachedData) {
          console.log('Using cached data as fallback');
          setUniqueValues(cachedData.data);
          setCacheStatus({
            isUsingCache: true,
            isRefreshing: false,
            lastRefresh: new Date(cachedData.timestamp),
            cacheAge: Math.floor((Date.now() - cachedData.timestamp) / 60000)
          });
          return;
        }
      }
      
      setError('Failed to fetch unique values');
      setCacheStatus(prev => ({ ...prev, isRefreshing: false }));
    }
  }, [dataSource]);

  // Fetch available course groups when location and date change
  const fetchAvailableCourseGroups = useCallback(async (location: string, date: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/course-groups?location=${encodeURIComponent(location)}&date=${encodeURIComponent(date)}&data_source=${dataSource}`
      );
      setAvailableCourseGroups(response.data.course_groups || []);
    } catch (err) {
      console.error('Failed to fetch course groups:', err);
      setAvailableCourseGroups([]);
    }
  }, [dataSource]);

  // Fetch filtered appointments
  const fetchAppointments = useCallback(async () => {
    if (!filters.location || !filters.startDate) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    
    // Add loading animation for existing elements
    const existingElements = document.querySelectorAll('.student-row, .student-card');
    if (existingElements.length > 0) {
      gsap.to(existingElements, {
        opacity: 0.3,
        duration: 0.3,
        ease: "power2.out"
      });
    }

    try {
      const params = new URLSearchParams({
        location: filters.location,
        start_date: filters.startDate.toISOString().split('T')[0],
        data_source: dataSource,
      });

      if (filters.courseTypes.length > 0) {
        params.append('course_types', filters.courseTypes.join(','));
      }

      const response = await axios.get(`${API_BASE_URL}/appointments?${params}`);
      setAppointments(response.data || []);
      setError(null);
      
      // Animate in new data
      setTimeout(() => {
        const newElements = document.querySelectorAll('.student-row, .student-card');
        gsap.fromTo(newElements, 
          { opacity: 0, y: 10 },
          { 
            opacity: 1, 
            y: 0, 
            duration: 0.4, 
            stagger: 0.02,
            ease: "power2.out" 
          }
        );
      }, 100);
      
    } catch (err) {
      setError('Failed to fetch appointments');
      console.error(err);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, dataSource]);

  // Enhanced refresh data with cache clearing
  const refreshData = async (clearCacheFirst: boolean = false) => {
    setRefreshing(true);
    
    try {
      if (clearCacheFirst) {
        clearCache(dataSource);
      }
      
      await axios.post(`${API_BASE_URL}/refresh`, { data_source: dataSource });

      // Refresh unique values and current appointments
      await fetchUniqueValues(false); // Force fresh fetch
      if (filters.location && filters.startDate) {
        await fetchAppointments();
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to refresh data');
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Function to check awards for students with LSS IDs
  const checkAwards = useCallback(async () => {
    console.log('=== checkAwards function called ===');
    console.log('appointments.length:', appointments.length);
    if (!appointments.length) {
      console.log('No appointments found, exiting early');
      return;
    }

    // Filter students with LSS IDs
    const studentsWithLSSIds = appointments.filter(
      appointment => appointment['LSS ID'] && appointment['LSS ID'].trim()
    );

    if (studentsWithLSSIds.length === 0) {
      setAwardCheckStatus(prev => ({
        ...prev,
        error: 'No students with LSS IDs found'
      }));
      return;
    }

    setAwardCheckStatus(prev => ({
      ...prev,
      isChecking: true,
      error: null
    }));

    try {
      // Prepare student data for API
      const studentsData: AttendanceStudent[] = studentsWithLSSIds.map(appointment => ({
        student_name: appointment['Student Name'],
        lss_id: appointment['LSS ID'],
        course_type: appointment['Course Type'],
        email: appointment['Email'],
        date_of_birth: appointment['Date of Birth'] || '',
        course_end_date: appointment['Course Start Date'] || ''
      }));

      console.log('=== DEBUGGING STUDENT DATA PREPARATION ===');
      console.log('All appointments:', appointments.length);
      console.log('Students with LSS IDs:', studentsWithLSSIds.length);
      console.log('Student data being sent to API:');
      studentsData.forEach((student, index) => {
        console.log(`  ${index + 1}. ${student.student_name} (LSS ID: "${student.lss_id}") - Course: ${student.course_type}`);
      });
      console.log('=== END DEBUG ===');

      // Each student will have their individual course type checked
      const courseTypeSet = new Set<string>();
      studentsData.forEach(s => courseTypeSet.add(s.course_type));
      const uniqueCourseTypes = Array.from(courseTypeSet);
      console.log(`Checking prerequisites for ${studentsData.length} students across ${uniqueCourseTypes.length} course types:`, uniqueCourseTypes);

      const response = await axios.post<AttendanceAwardCheckResponse>('/api/attendance/check-awards', {
        students: studentsData
      });

      setAwardCheckStatus(prev => ({
        ...prev,
        isChecking: false,
        results: response.data.results,
        lastCheck: new Date(),
        error: null
      }));

      console.log(`Award check completed for ${response.data.total_students} students in ${response.data.search_time} seconds`);
      
    } catch (err: any) {
      console.error('Award check failed:', err);
      setAwardCheckStatus(prev => ({
        ...prev,
        isChecking: false,
        error: err.response?.data?.error || 'Failed to check awards'
      }));
    }
  }, [appointments]);

  // Function to get award check result for a specific student
  const getAwardCheckResult = useCallback((lssId: string): AttendanceAwardCheckResult | null => {
    return awardCheckStatus.results.find(result => result['LSS ID'] === lssId) || null;
  }, [awardCheckStatus.results]);

  // Handle data source change (simplified for Google Sheets only)
  const handleDataSourceChange = async (newDataSource: string) => {
    console.log('Switching data source to:', newDataSource);
    setDataSource(newDataSource);
    
    // Clear current data when switching sources
    setAppointments([]);
    setAvailableCourseGroups([]);
    setFilters({ courseTypes: [], location: '', startDate: null });
    setError(null);
    
    // Reset cache status
    setCacheStatus({
      isUsingCache: false,
      isRefreshing: false,
      lastRefresh: null,
      cacheAge: 0
    });
    
    // Fetch data from Google Sheets
    setLoading(true);
    try {
      await fetchUniqueValues(true);
    } catch (err: any) {
      console.error('Failed to fetch unique values for new data source:', err);
      setError(`Failed to fetch data from Google Sheets. ${err.response?.data?.error || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (column: keyof Appointment) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle classroom organization with age sorting and friend group preservation
  const splitIntoClassrooms = () => {
    const students = [...sortedAppointments].map((student, index) => ({
      ...student,
      id: `student-${student._row_number}-${index}` // Create unique ID
    }));
    
    const classrooms: (Appointment & { id: string })[][] = Array.from({ length: numClassrooms }, () => []);
    
    // Enhanced friend group detection that handles cross-referencing (A lists B, B lists A)
    const friendGroups: { [key: string]: (Appointment & { id: string })[] } = {};
    const individualStudents: (Appointment & { id: string })[] = [];
    const processedStudents = new Set<string>();
    
    // First pass: identify all students with valid friend groups
    const studentsWithFriendGroups = students.filter(student => 
      isValidFriendGroup(student['Friend Group'])
    );
    
    // Create a lookup map for faster searching
    const studentByName = new Map<string, (Appointment & { id: string })>();
    students.forEach(student => {
      studentByName.set(student['Student Name'], student);
    });
    
    studentsWithFriendGroups.forEach(student => {
      const studentName = student['Student Name'];
      const friendGroupName = student['Friend Group'];
      
      // Skip if already processed
      if (processedStudents.has(studentName)) {
        return;
      }
      
      // Check if this is a cross-reference situation
      const friendStudent = studentByName.get(friendGroupName);
      const isCrossReference = friendStudent && 
                              friendStudent['Friend Group'] === studentName &&
                              isValidFriendGroup(friendStudent['Friend Group']);
      
      if (isCrossReference) {
        // Create a unified group name (alphabetically sorted for consistency)
        const groupName = [studentName, friendGroupName].sort().join(' & ');
        
        if (!friendGroups[groupName]) {
          friendGroups[groupName] = [];
        }
        
        friendGroups[groupName].push(student);
        friendGroups[groupName].push(friendStudent!);
        
        // Mark both as processed
        processedStudents.add(studentName);
        processedStudents.add(friendGroupName);
        
        console.log(`Cross-reference detected: ${studentName} ↔ ${friendGroupName} → Group: "${groupName}"`);
      } else {
        // Regular friend group (multiple people might list the same friend group name)
        if (!friendGroups[friendGroupName]) {
          friendGroups[friendGroupName] = [];
        }
        friendGroups[friendGroupName].push(student);
        processedStudents.add(studentName);
      }
    });
    
    // Add students without friend groups to individual students
    students.forEach(student => {
      if (!processedStudents.has(student['Student Name'])) {
        individualStudents.push(student);
      }
    });
    
    // Debug logging to help verify friend group identification
    console.log('Friend Groups Found:');
    Object.entries(friendGroups).forEach(([groupName, members]) => {
      console.log(`  "${groupName}": ${members.length} students - ${members.map(s => s['Student Name']).join(', ')}`);
    });
    console.log(`Individual Students: ${individualStudents.length} students`);
    
    if (sortByAge) {
      // Sort individual students by age (Date of Birth)
      individualStudents.sort((a, b) => {
        const dateA = a['Date of Birth'] ? new Date(a['Date of Birth']) : new Date(0);
        const dateB = b['Date of Birth'] ? new Date(b['Date of Birth']) : new Date(0);
        return dateA.getTime() - dateB.getTime();
      });
      
      // Sort friend groups by the average age of the group
      const sortedFriendGroups = Object.entries(friendGroups).sort(([, groupA], [, groupB]) => {
        const avgAgeA = groupA.reduce((sum, student) => {
          const date = student['Date of Birth'] ? new Date(student['Date of Birth']).getTime() : 0;
          return sum + date;
        }, 0) / groupA.length;
        
        const avgAgeB = groupB.reduce((sum, student) => {
          const date = student['Date of Birth'] ? new Date(student['Date of Birth']).getTime() : 0;
          return sum + date;
        }, 0) / groupB.length;
        
        return avgAgeA - avgAgeB;
      });
      
      // Distribute sorted students and friend groups
      let currentClassroom = 0;
      
      // Place friend groups first (keeping each group together)
      sortedFriendGroups.forEach(([, group]) => {
        classrooms[currentClassroom].push(...group);
        currentClassroom = (currentClassroom + 1) % numClassrooms;
      });
      
      // Place individual students
      individualStudents.forEach(student => {
        classrooms[currentClassroom].push(student);
        currentClassroom = (currentClassroom + 1) % numClassrooms;
      });
      
    } else {
      // Even without age sorting, keep friend groups together
      let currentClassroom = 0;
      
      // Place friend groups first (keeping each group together)
      Object.entries(friendGroups).forEach(([, group]) => {
        classrooms[currentClassroom].push(...group);
        currentClassroom = (currentClassroom + 1) % numClassrooms;
      });
      
      // Place individual students
      individualStudents.forEach(student => {
        classrooms[currentClassroom].push(student);
        currentClassroom = (currentClassroom + 1) % numClassrooms;
      });
    }
    
    setClassroomView({
      enabled: true,
      numClassrooms,
      classrooms
    });

    // Close dialog and reset states
    setShowSplitDialog(false);
    setMoveMode(false);
    setSelectedStudents(new Set());

    // Animate classroom appearance with GSAP
    setTimeout(() => {
      classroomRefs.current.forEach((ref, index) => {
        if (ref) {
          gsap.fromTo(ref, 
            { 
              opacity: 0, 
              y: 50,
              scale: 0.95
            },
            { 
              opacity: 1, 
              y: 0,
              scale: 1,
              duration: 0.6,
              delay: index * 0.2,
              ease: "back.out(1.7)"
            }
          );
        }
      });
    }, 50);
  };

  // Handle checkbox selection
  const handleStudentSelect = (studentId: string, checked: boolean) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(studentId);
      } else {
        newSet.delete(studentId);
      }
      return newSet;
    });
  };

  // Handle select all/none
  const handleSelectAll = (classroomIndex: number, checked: boolean) => {
    const classroom = classroomView.classrooms[classroomIndex];
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      classroom.forEach(student => {
        if (checked) {
          newSet.add(student.id);
        } else {
          newSet.delete(student.id);
        }
      });
      return newSet;
    });
  };

  // Handle bulk move
  const handleBulkMove = () => {
    if (selectedStudents.size === 0) return;
    setShowMoveDialog(true);
  };

  // Execute bulk move
  const executeBulkMove = () => {
    if (targetClassroom === null) return;

    const newClassrooms = [...classroomView.classrooms];
    const studentsToMove: (Appointment & { id: string })[] = [];

    // Remove selected students from their current classrooms
    newClassrooms.forEach((classroom, classroomIndex) => {
      for (let i = classroom.length - 1; i >= 0; i--) {
        if (selectedStudents.has(classroom[i].id)) {
          studentsToMove.push(classroom[i]);
          classroom.splice(i, 1);
        }
      }
    });

    // Add students to target classroom
    newClassrooms[targetClassroom].push(...studentsToMove);

    // Capture state before change for Flip animation
    const state = Flip.getState(".student-card, .classroom-container");

    setClassroomView(prev => ({
      ...prev,
      classrooms: newClassrooms
    }));

    // Clear selections and close dialog
    setSelectedStudents(new Set());
    setShowMoveDialog(false);
    setTargetClassroom(null);

    // Animate the change with Flip
    setTimeout(() => {
      Flip.from(state, {
        duration: 0.8,
        ease: "power2.inOut",
        stagger: 0.05,
        onComplete: () => {
          // Animate classroom count updates
          classroomRefs.current.forEach((ref) => {
            if (ref) {
              gsap.to(ref.querySelector('h6'), {
                scale: 1.05,
                duration: 0.2,
                yoyo: true,
                repeat: 1,
                ease: "power2.inOut"
              });
            }
          });
        }
      });
    }, 10);
  };

  const resetClassroomView = () => {
    // Animate classrooms out before switching view
    classroomRefs.current.forEach((ref, index) => {
      if (ref) {
        gsap.to(ref, {
          opacity: 0,
          y: -30,
          scale: 0.95,
          duration: 0.4,
          delay: index * 0.1,
          ease: "power2.in"
        });
      }
    });

    // Switch view after animation completes
    setTimeout(() => {
      setClassroomView({
        enabled: false,
        numClassrooms: 2,
        classrooms: []
      });

      // Clear all states
      setSelectedStudents(new Set());
      setShowMoveDialog(false);
      setTargetClassroom(null);
      setMoveMode(false);
      setShowSplitDialog(false);
      setSortByAge(false);
      setNumClassrooms(2);

      // Animate main table back in
      if (totalStudentsRef.current) {
        gsap.fromTo(totalStudentsRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
        );
      }
    }, 500);
  };

  // Helper function to render a table cell based on column type
  const renderTableCell = (appointment: Appointment, columnKey: string) => {
    const value = appointment[columnKey as keyof Appointment];
    
    switch (columnKey) {
      case 'Student Name':
        const awardResult = getAwardCheckResult(appointment['LSS ID']);
        const hasLssId = appointment['LSS ID'] && appointment['LSS ID'].trim();
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title={
                <Box sx={{ maxWidth: 400 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Appointment Details
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Start Date:</b> {(() => {
                      const startDate = appointment['Course Start Date'] || '';
                      const dateMatch = startDate.match(/(\w+,?\s+\w+\s+\d{1,2},\s+\d{4})/);
                      return dateMatch ? dateMatch[1] : startDate || 'N/A';
                    })()}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Date of Birth:</b> {appointment['Date of Birth'] || ''}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>LSS ID:</b> {appointment['LSS ID'] || ''}
                  </Typography>
                  
                  {shouldShowFriendGroupIcon(appointment['Student Name'], appointment['Friend Group'], sortedAppointments) && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <b>Friend Group:</b> {isValidFriendGroup(appointment['Friend Group']) ? appointment['Friend Group'] : 'Listed by others'}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Full Schedule:</b>
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1, 
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      padding: 1,
                      borderRadius: 1
                    }}
                  >
                    {appointment['Course Schedule'] || ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Date Scheduled:</b> {appointment['Date Scheduled'] || ''}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    <b>Google Sheet Row:</b> {appointment['_row_number']}
                  </Typography>
                </Box>
              }
              arrow
              placement="right"
              sx={{ maxWidth: 500 }}
            >
              <InfoOutlinedIcon 
                sx={{ 
                  fontSize: 18, 
                  color: 'primary.main', 
                  cursor: 'pointer' 
                }} 
              />
            </Tooltip>
            {value}
            {shouldShowFriendGroupIcon(appointment['Student Name'], appointment['Friend Group'], sortedAppointments) && (
              <Tooltip
                title={getFriendGroupTooltip(appointment['Student Name'], appointment['Friend Group'], sortedAppointments)}
                arrow
                placement="top"
              >
                <PeopleIcon 
                  sx={{ 
                    fontSize: 16, 
                    color: 'primary.main',
                    cursor: 'pointer'
                  }} 
                />
              </Tooltip>
            )}
            {/* Award Status Indicator */}
            {hasLssId && (
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Prerequisite Status
                    </Typography>
                    {awardCheckStatus.isChecking ? (
                      <Typography variant="body2">Checking prerequisites...</Typography>
                    ) : awardResult ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Status:</b> {awardResult.PrerequisitesMet ? 'Prerequisites Met ✓' : 'Prerequisites Not Met ✗'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Awards Checked:</b> {awardResult.CheckedAwards.join(', ')}
                        </Typography>
                        {Object.entries(awardResult.Matches).map(([award, hasAward]) => (
                          <Typography key={award} variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {hasAward ? '✓' : '✗'} {award}
                            {hasAward && awardResult.ExpiryDetails[award] && (
                              <span style={{ color: awardResult.ExpiryDetails[award].daysLeft < 30 ? 'orange' : 'inherit' }}>
                                {' '}(expires: {awardResult.ExpiryDetails[award].expiry})
                              </span>
                            )}
                          </Typography>
                        ))}
                        {awardResult.AgeAtCourseEnd !== null && (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                            {awardResult.AgeRequirementsMet ? '✓' : '✗'} Age Requirement
                            {Object.entries(awardResult.AgeRequirements).length > 0 && (
                              <span>
                                {' '}(will be {awardResult.AgeAtCourseEnd} at course end, min: {Object.values(awardResult.AgeRequirements)[0]})
                              </span>
                            )}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          Last checked: {awardCheckStatus.lastCheck?.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        Award check pending... LSS ID: {appointment['LSS ID']}
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                {awardCheckStatus.isChecking ? (
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                ) : awardResult ? (
                  awardResult.PrerequisitesMet ? (
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 16, 
                        color: 'success.main',
                        cursor: 'pointer'
                      }} 
                    />
                  ) : (
                    <CancelIcon 
                      sx={{ 
                        fontSize: 16, 
                        color: 'error.main',
                        cursor: 'pointer'
                      }} 
                    />
                  )
                ) : (
                  <HelpOutlineIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: 'warning.main',
                      cursor: 'pointer'
                    }} 
                  />
                )}
              </Tooltip>
            )}
          </Box>
        );
      
      case 'Prerequisites':
        const prerequisiteResult = getAwardCheckResult(appointment['LSS ID']);
        const hasLssIdForPrereq = appointment['LSS ID'] && appointment['LSS ID'].trim();
        
        if (!hasLssIdForPrereq) {
          return (
            <Chip
              label="No LSS ID"
              size="small"
              color="default"
              variant="outlined"
            />
          );
        }
        
        if (awardCheckStatus.isChecking) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Checking...</Typography>
            </Box>
          );
        }
        
        if (!prerequisiteResult) {
          return (
            <Chip
              icon={<HelpOutlineIcon />}
              label="Pending"
              size="small"
              color="warning"
              variant="outlined"
            />
          );
        }
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Detailed Prerequisites Status
                </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b>Awards Checked:</b> {prerequisiteResult.CheckedAwards.join(', ')}
                        </Typography>
                        {Object.entries(prerequisiteResult.Matches).map(([award, hasAward]) => (
                          <Typography key={award} variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {hasAward ? '✓' : '✗'} {award}
                            {hasAward && prerequisiteResult.ExpiryDetails[award] && (
                              <span style={{ color: prerequisiteResult.ExpiryDetails[award].daysLeft < 30 ? 'orange' : 'inherit' }}>
                                {' '}(expires: {prerequisiteResult.ExpiryDetails[award].expiry})
                              </span>
                            )}
                          </Typography>
                        ))}
                        {prerequisiteResult.GroupEvaluationResults && Object.keys(prerequisiteResult.GroupEvaluationResults).length > 1 && (
                          <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              Logic Groups:
                            </Typography>
                            {Object.entries(prerequisiteResult.GroupEvaluationResults).map(([groupName, groupResult]: [string, any]) => (
                              <Typography key={groupName} variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                                {groupResult.met ? '✓' : '✗'} {groupName}: {groupResult.awards.join(', ')} 
                                {groupResult.age_requirements.length > 0 && ` (age ${groupResult.age_requirements.join(', ')}+)`}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {prerequisiteResult.AgeAtCourseEnd !== null && (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                            {prerequisiteResult.AgeRequirementsMet ? '✓' : '✗'} Age Requirement
                            {Object.entries(prerequisiteResult.AgeRequirements).length > 0 && (
                              <span>
                                {' '}(will be {prerequisiteResult.AgeAtCourseEnd} at course end, min: {Object.values(prerequisiteResult.AgeRequirements)[0]})
                              </span>
                            )}
                          </Typography>
                        )}
              </Box>
            }
            arrow
            placement="left"
          >
            <Chip
              icon={prerequisiteResult.PrerequisitesMet ? <CheckCircleIcon /> : <CancelIcon />}
              label={prerequisiteResult.PrerequisitesMet ? 'Met' : 'Not Met'}
              size="small"
              color={prerequisiteResult.PrerequisitesMet ? 'success' : 'error'}
              variant="outlined"
            />
          </Tooltip>
        );
      
      case 'Email':
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>Email:</b> {value}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EmailIcon />}
                  onClick={() => {
                    const subject = encodeURIComponent(`LSS Course: ${appointment['Course Type']}`);
                    const body = encodeURIComponent(`Hi ${appointment['Student Name']},\n\nRegarding your upcoming course: ${appointment['Course Type']}\n\nBest regards`);
                    const mailtoLink = `mailto:${value}?subject=${subject}&body=${body}${user?.email ? `&from=${user.email}` : ''}`;
                    window.open(mailtoLink, '_blank');
                  }}
                  sx={{ mt: 1 }}
                >
                  Send Email
                </Button>
              </Box>
            }
            arrow
            placement="left"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <EmailIcon 
                sx={{ 
                  fontSize: 18, 
                  color: 'primary.main',
                  mr: 1
                }} 
              />
              {value}
            </Box>
          </Tooltip>
        );
      
      case 'Course Start Date':
        // Extract clean date from "Saturday, July 19, 2025, :: "
        const startDate = value as string || '';
        const dateMatch = startDate.match(/(\w+,?\s+\w+\s+\d{1,2},\s+\d{4})/);
        return dateMatch ? dateMatch[1] : value || '';
      
      case 'Notes':
      case 'Course Schedule':
        return (
          <Tooltip title={value || ''} arrow>
            <Box sx={{ 
              maxWidth: 200, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {value || ''}
            </Box>
          </Tooltip>
        );
      
      default:
        return value || '';
    }
  };

  // Sort appointments based on current sorting
  const sortedAppointments = React.useMemo(() => {
    if (!sorting.column) return appointments;

    return [...appointments].sort((a, b) => {
      const aValue = a[sorting.column!];
      const bValue = b[sorting.column!];
      
      // Handle Date of Birth sorting (convert to Date for proper comparison)
      if (sorting.column === 'Date of Birth') {
        const dateA = aValue ? new Date(aValue as string) : new Date(0);
        const dateB = bValue ? new Date(bValue as string) : new Date(0);
        const comparison = dateA.getTime() - dateB.getTime();
        return sorting.direction === 'asc' ? comparison : -comparison;
      }
      
      // Handle string sorting
      const stringA = String(aValue || '').toLowerCase();
      const stringB = String(bValue || '').toLowerCase();
      
      if (stringA < stringB) return sorting.direction === 'asc' ? -1 : 1;
      if (stringA > stringB) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [appointments, sorting]);

  // Reset sorting when appointments change
  useEffect(() => {
    setSorting({ column: null, direction: 'asc' });
  }, [appointments]);

  // Initialize data on mount
  useEffect(() => {
    if (user) {
      fetchDataSourceInfo();
    }
    setLoading(false);
    
    // Add entrance animation for the main container
    gsap.fromTo('.attendance-container', 
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
    );
  }, [fetchDataSourceInfo, user]);

  // Fetch unique values when data source changes
  useEffect(() => {
    if (user && dataSource) {
      fetchUniqueValues();
    }
  }, [fetchUniqueValues, user, dataSource]);

  // Fetch appointments when filters change
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Fetch available course groups when location and date change
  useEffect(() => {
    if (filters.location && filters.startDate) {
      const dateStr = filters.startDate.toISOString().split('T')[0];
      fetchAvailableCourseGroups(filters.location, dateStr);
      // Reset course type when location or date changes
      setFilters(prev => ({ ...prev, courseTypes: [] }));
    } else {
      setAvailableCourseGroups([]);
    }
  }, [filters.location, filters.startDate, fetchAvailableCourseGroups]);

  // Show loading if auth is still loading or user is not authenticated
  if (authLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Please log in to access the Attendance Management system.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} className="attendance-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" component="h1">
            Attendance Management
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Data Source:
            </Typography>
            <Chip 
              label="Google Sheets"
              color="default"
              size="small"
            />
            
            {/* Cache Status Indicators */}
            {cacheStatus.isUsingCache && (
              <Tooltip 
                title={`Using cached data from ${cacheStatus.lastRefresh?.toLocaleTimeString() || 'unknown time'} (${cacheStatus.cacheAge} min ago)`}
                arrow
              >
                <Chip
                  icon={<CachedIcon />}
                  label={`Cached (${cacheStatus.cacheAge}m)`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
            
            {cacheStatus.isRefreshing && (
              <Tooltip title="Refreshing data in background" arrow>
                <Chip
                  icon={<CloudSyncIcon />}
                  label="Syncing"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}

            {/* Award Check Status */}
            {awardCheckStatus.isChecking && (
              <Tooltip title="Checking student prerequisites" arrow>
                <Chip
                  icon={<CircularProgress size={12} />}
                  label="Checking Awards"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}

            {awardCheckStatus.lastCheck && !awardCheckStatus.isChecking && (
              <Tooltip 
                title={`Last award check: ${awardCheckStatus.lastCheck.toLocaleString()}\n${awardCheckStatus.results.length} students checked`}
                arrow
              >
                <Chip
                  icon={<TaskAltIcon />}
                  label={`Awards Checked (${awardCheckStatus.results.length})`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Tooltip>
            )}

            {awardCheckStatus.error && (
              <Tooltip title={awardCheckStatus.error} arrow>
                <Chip
                  icon={<CancelIcon />}
                  label="Award Check Failed"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {dataSourceInfo && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Data Source</InputLabel>
              <Select
                value={dataSource}
                label="Data Source"
                onChange={(e) => handleDataSourceChange(e.target.value)}
              >
                {dataSourceInfo.available_sources.map((source) => (
                  <MenuItem 
                    key={source} 
                    value={source}
                    disabled={
                      (source === 'google_sheets' && !dataSourceInfo.google_sheets_configured) ||
                      (source === 'acuity' && !dataSourceInfo.acuity_configured) ||
                      (source === 'hybrid' && (!dataSourceInfo.google_sheets_configured || !dataSourceInfo.acuity_configured))
                    }
                  >
                    {source === 'google_sheets' ? 'Google Sheets' : 
                     source === 'acuity' ? 'Acuity Direct' : 
                     'Hybrid (Google Sheets + Acuity)'}
                    {((source === 'google_sheets' && !dataSourceInfo.google_sheets_configured) ||
                      (source === 'acuity' && !dataSourceInfo.acuity_configured) ||
                      (source === 'hybrid' && (!dataSourceInfo.google_sheets_configured || !dataSourceInfo.acuity_configured))) && ' (Not Configured)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refreshData()}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<TaskAltIcon />}
              onClick={() => {
                console.log('=== Check Prerequisites button clicked ===');
                console.log('Button disabled?', awardCheckStatus.isChecking || appointments.length === 0);
                console.log('awardCheckStatus.isChecking:', awardCheckStatus.isChecking);
                console.log('appointments.length:', appointments.length);
                checkAwards();
              }}
              disabled={awardCheckStatus.isChecking || appointments.length === 0}
            >
              {awardCheckStatus.isChecking ? 'Checking Awards...' : 'Check Prerequisites'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => {
                setTempVisibleColumns([...visibleColumns]);
                setShowColumnSelector(true);
              }}
            >
              Customize Columns
            </Button>
            

          </Box>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Location"
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
            >
              <MenuItem value="">Select Location</MenuItem>
              {uniqueValues.locations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="date"
              fullWidth
              label="Session Date"
              value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const dateValue = e.target.value ? new Date(e.target.value) : null;
                setFilters(prev => ({ ...prev, startDate: dateValue }));
              }}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                if (input) input.showPicker?.();
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Grid>
          {filters.location && filters.startDate && availableCourseGroups.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Course Types</InputLabel>
                <Select
                  multiple
                  value={filters.courseTypes}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setFilters(prev => ({ ...prev, courseTypes: value }));
                  }}
                  label="Course Types"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          All Course Types
                        </Typography>
                      ) : (
                        selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))
                      )}
                    </Box>
                  )}
                >
                  {availableCourseGroups.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={filters.courseTypes.indexOf(type) > -1} />
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Student Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 1 }} />
          Student Search
        </Typography>
        
        <TextField
          fullWidth
          label="Search students by name"
          placeholder="Enter student name to search..."
          value={studentSearch.searchTerm}
          onChange={handleSearchInputChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: studentSearch.isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        {studentSearch.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {studentSearch.error}
          </Alert>
        )}

        {studentSearch.results.length > 0 && (
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            <List>
              {studentSearch.results.map((student, index) => (
                <React.Fragment key={student.profile.id}>
                  <ListItem
                    button
                    onClick={() => handleSearchStudentSelect(student)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={`${student.profile.firstName} ${student.profile.lastName}`}
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" color="text.secondary">
                            {student.profile.email}
                          </Typography>
                          <Badge 
                            badgeContent={student.appointment_count} 
                            color="primary"
                            sx={{ ml: 1 }}
                          >
                            <EventIcon fontSize="small" />
                          </Badge>
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            {student.appointment_count} appointment{student.appointment_count !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < studentSearch.results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {studentSearch.searchTerm.length >= 2 && studentSearch.results.length === 0 && !studentSearch.isSearching && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No students found matching "{studentSearch.searchTerm}"
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {uniqueValues.locations.length === 0 && !error ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading data from Google Sheets...
          </Typography>
        </Paper>
      ) : loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : filters.location && filters.startDate ? (
        appointments.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Total Students: {sortedAppointments.length}
                </Typography>
                {awardCheckStatus.results.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    {(() => {
                      const studentsWithPrereqs = awardCheckStatus.results.filter(r => r.PrerequisitesMet).length;
                      const studentsWithoutPrereqs = awardCheckStatus.results.filter(r => !r.PrerequisitesMet).length;
                      const studentsWithLssIds = appointments.filter(a => a['LSS ID'] && a['LSS ID'].trim()).length;
                      
                      return (
                        <>
                          {studentsWithPrereqs > 0 && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`${studentsWithPrereqs} Prerequisites Met`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {studentsWithoutPrereqs > 0 && (
                            <Chip
                              icon={<CancelIcon />}
                              label={`${studentsWithoutPrereqs} Missing Prerequisites`}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {studentsWithLssIds > awardCheckStatus.results.length && (
                            <Chip
                              icon={<HelpOutlineIcon />}
                              label={`${studentsWithLssIds - awardCheckStatus.results.length} Not Checked`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {!classroomView.enabled ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowSplitDialog(true)}
                  >
                    Split Classroom
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={resetClassroomView}
                    >
                      Show All Students
                    </Button>
                    {!moveMode ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setMoveMode(true)}
                        sx={{ ml: 1 }}
                      >
                        Move Students
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setMoveMode(false);
                            setSelectedStudents(new Set());
                          }}
                          sx={{ ml: 1 }}
                        >
                          Cancel Move
                        </Button>
                        {selectedStudents.size > 0 && (
                          <>
                            <Chip 
                              label={`${selectedStudents.size} selected`} 
                              color="primary" 
                              size="small" 
                              sx={{ ml: 1 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleBulkMove}
                              color="secondary"
                              sx={{ ml: 1 }}
                            >
                              Move Selected
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {classroomView.enabled ? (
              <Box sx={{ mb: 2 }}>
                {classroomView.classrooms.map((classroom, classroomIndex) => (
                  <Paper 
                    key={classroomIndex}
                    ref={(el) => classroomRefs.current[classroomIndex] = el}
                    className="classroom-container"
                    sx={{ 
                      mb: 3,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <Box sx={{ p: 2, pb: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" sx={{ color: 'primary.main' }}>
                              Classroom {classroomIndex + 1} ({classroom.length} students)
                            </Typography>
                            {(() => {
                              const ageIndicator = getAgeGroupIndicator(classroom);
                              return (
                                <Tooltip 
                                  title={ageIndicator.tooltip} 
                                  arrow 
                                  placement="top"
                                >
                                  <Chip
                                    label={ageIndicator.label}
                                    size="small"
                                    color={ageIndicator.color}
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      height: '24px'
                                    }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'normal' }}>
                            Use checkboxes to select students for bulk operations
                          </Typography>
                        </Box>
                        {moveMode && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={classroom.every(student => selectedStudents.has(student.id))}
                                indeterminate={classroom.some(student => selectedStudents.has(student.id)) && !classroom.every(student => selectedStudents.has(student.id))}
                                onChange={(e) => handleSelectAll(classroomIndex, e.target.checked)}
                              />
                            }
                            label="Select All"
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {moveMode && <TableCell>Select</TableCell>}
                            {visibleColumns.map((columnKey) => {
                              const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
                              return (
                                <TableCell key={columnKey}>
                                  {column?.label || columnKey}
                                </TableCell>
                              );
                            })}
                            {visibleColumns.includes('Email') && <TableCell>Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {classroom.map((appointment, index) => (
                            <TableRow 
                              key={appointment.id}
                              className="student-card"
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  transition: 'all 0.2s ease'
                                },
                                '&:nth-of-type(even)': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                },
                                backgroundColor: selectedStudents.has(appointment.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                              }}
                            >
                              {moveMode && (
                                <TableCell>
                                  <Checkbox
                                    checked={selectedStudents.has(appointment.id)}
                                    onChange={(e) => handleStudentSelect(appointment.id, e.target.checked)}
                                    size="small"
                                  />
                                </TableCell>
                              )}
                              {visibleColumns.map((columnKey) => (
                                <TableCell key={columnKey}>
                                  {renderTableCell(appointment, columnKey)}
                                </TableCell>
                              ))}
                              {visibleColumns.includes('Email') && (
                                <TableCell>
                                  <Tooltip
                                    title={
                                      <Box sx={{ p: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <b>Email:</b> {appointment['Email']}
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          startIcon={<EmailIcon />}
                                          onClick={() => {
                                            const subject = encodeURIComponent(`LSS Course: ${appointment['Course Type']}`);
                                            const body = encodeURIComponent(`Hi ${appointment['Student Name']},\n\nRegarding your upcoming course: ${appointment['Course Type']}\n\nBest regards`);
                                            const mailtoLink = `mailto:${appointment['Email']}?subject=${subject}&body=${body}${user?.email ? `&from=${user.email}` : ''}`;
                                            window.open(mailtoLink, '_blank');
                                          }}
                                          sx={{ mt: 1 }}
                                        >
                                          Send Email
                                        </Button>
                                      </Box>
                                    }
                                    arrow
                                    placement="left"
                                  >
                                    <EmailIcon 
                                      sx={{ 
                                        fontSize: 18, 
                                        color: 'primary.main',
                                        cursor: 'pointer'
                                      }} 
                                    />
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                          {classroom.length === 0 && (
                            <TableRow>
                              <TableCell 
                                colSpan={
                                  visibleColumns.length + 
                                  (moveMode ? 1 : 0) + 
                                  (visibleColumns.includes('Email') ? 1 : 0)
                                } 
                                sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontStyle: 'italic' }}
                              >
                                Drop students here
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))}
              </Box>
                          ) : (
                <TableContainer 
                  component={Paper}
                  ref={totalStudentsRef}
                >
                <Table>
                <TableHead>
                  <TableRow>
                    {visibleColumns.map((columnKey) => {
                      const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
                      const isSortable = ['Student Name', 'Course Type', 'Date of Birth', 'LSS ID', 'Course Start Date', 'Location'].includes(columnKey);
                      
                      return (
                        <TableCell key={columnKey}>
                          {isSortable ? (
                            <TableSortLabel
                              active={sorting.column === columnKey}
                              direction={sorting.column === columnKey ? sorting.direction : 'asc'}
                              onClick={() => handleSort(columnKey as keyof Appointment)}
                            >
                              {column?.label || columnKey}
                            </TableSortLabel>
                          ) : (
                            column?.label || columnKey
                          )}
                        </TableCell>
                      );
                    })}
                    {visibleColumns.includes('Email') && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAppointments.map((appointment, index) => (
                    <TableRow 
                      key={index} 
                      className="student-row"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          transform: 'translateX(2px)',
                          transition: 'all 0.2s ease'
                        }
                      }}
                    >
                      {visibleColumns.map((columnKey) => (
                        <TableCell key={columnKey}>
                          {renderTableCell(appointment, columnKey)}
                        </TableCell>
                      ))}
                      {visibleColumns.includes('Email') && (
                        <TableCell>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <b>Email:</b> {appointment['Email']}
                                </Typography>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<EmailIcon />}
                                  onClick={() => {
                                    const subject = encodeURIComponent(`LSS Course: ${appointment['Course Type']}`);
                                    const body = encodeURIComponent(`Hi ${appointment['Student Name']},\n\nRegarding your upcoming course: ${appointment['Course Type']}\n\nBest regards`);
                                    const mailtoLink = `mailto:${appointment['Email']}?subject=${subject}&body=${body}${user?.email ? `&from=${user.email}` : ''}`;
                                    window.open(mailtoLink, '_blank');
                                  }}
                                  sx={{ mt: 1 }}
                                >
                                  Send Email
                                </Button>
                              </Box>
                            }
                            arrow
                            placement="left"
                          >
                            <EmailIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: 'primary.main',
                                cursor: 'pointer'
                              }} 
                            />
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </>
        ) : (
          <Typography sx={{ mt: 2 }}>No appointments found for the selected filters.</Typography>
        )
      ) : (
        <Typography sx={{ mt: 2 }}>
          Please select a location and session date to view appointments.
        </Typography>
      )}

      {/* Split Classroom Dialog */}
      <Dialog 
        open={showSplitDialog} 
        onClose={() => setShowSplitDialog(false)} 
        maxWidth="sm" 
        fullWidth
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>Split Classroom</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Configure how you want to split the students into different classrooms.
            </Typography>
            
            <TextField
              label="Number of Classrooms"
              type="text"
              value={numClassrooms}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setNumClassrooms(value === '' ? 2 : Math.max(2, Math.min(10, parseInt(value))));
                }
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              fullWidth
              sx={{ mb: 3 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={sortByAge}
                  onChange={(e) => setSortByAge(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Sort by age</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Place similarly aged students in the same classroom. Friend groups will stay together regardless of age.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSplitDialog(false)}>Cancel</Button>
          <Button 
            onClick={splitIntoClassrooms} 
            variant="contained"
          >
            Split Classroom
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Move Dialog */}
      <Dialog 
        open={showMoveDialog} 
        onClose={() => setShowMoveDialog(false)}
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>Move Selected Students</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Move {selectedStudents.size} selected student{selectedStudents.size !== 1 ? 's' : ''} to which classroom?
          </Typography>
          <RadioGroup
            value={targetClassroom}
            onChange={(e) => setTargetClassroom(parseInt(e.target.value))}
          >
            {Array.from({ length: classroomView.numClassrooms }, (_, index) => (
              <FormControlLabel
                key={index}
                value={index}
                control={<Radio />}
                label={`Classroom ${index + 1} (${classroomView.classrooms[index]?.length || 0} students)`}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMoveDialog(false)}>Cancel</Button>
          <Button 
            onClick={executeBulkMove} 
            variant="contained"
            disabled={targetClassroom === null}
          >
            Move Students
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Selector Dialog */}
      <Dialog 
        open={showColumnSelector} 
        onClose={() => setShowColumnSelector(false)}
        maxWidth="md"
        fullWidth
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>Customize Table Columns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Select which columns to display in the table. Selected columns will appear in the order shown below.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Selected Columns (in display order):
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {tempVisibleColumns.map((columnKey, index) => {
              const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
              return (
                <Box
                  key={columnKey}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }}
                >
                  <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                    {index + 1}. {column?.label || columnKey}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      const newOrder = [...tempVisibleColumns];
                      if (index > 0) {
                        [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
                        setTempVisibleColumns(newOrder);
                      }
                    }}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      const newOrder = [...tempVisibleColumns];
                      if (index < newOrder.length - 1) {
                        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                        setTempVisibleColumns(newOrder);
                      }
                    }}
                    disabled={index === tempVisibleColumns.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setTempVisibleColumns(prev => prev.filter(col => col !== columnKey));
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              );
            })}
            {tempVisibleColumns.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No columns selected. Please select columns from the list below.
              </Typography>
            )}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Available Columns:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {AVAILABLE_COLUMNS.filter(column => !tempVisibleColumns.includes(column.key)).map((column) => (
              <Box
                key={column.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {column.label}
                </Typography>
                {column.defaultVisible && (
                  <Chip 
                    label="Default" 
                    size="small" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTempVisibleColumns(prev => [...prev, column.key]);
                  }}
                >
                  Add
                </Button>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setTempVisibleColumns(AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key));
              }}
            >
              Reset to Default
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setTempVisibleColumns(AVAILABLE_COLUMNS.map(col => col.key));
              }}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setTempVisibleColumns([]);
              }}
            >
              Clear All
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setTempVisibleColumns([]);
              setShowColumnSelector(false);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setVisibleColumns([...tempVisibleColumns]);
              setShowColumnSelector(false);
            }}
            variant="contained"
            disabled={tempVisibleColumns.length === 0}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Details Modal */}
      <Dialog 
        open={studentSearch.showModal} 
        onClose={handleCloseStudentModal}
        maxWidth="md"
        fullWidth
        PaperProps={universalModalPaperProps}
        BackdropProps={universalModalBackdropProps}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              Student Information
            </Box>
            <IconButton onClick={handleCloseStudentModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {studentSearch.selectedStudent && (
            <Box>
              {/* Student Profile */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Profile Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {studentSearch.selectedStudent.profile.firstName} {studentSearch.selectedStudent.profile.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                      {studentSearch.selectedStudent.profile.email}
                    </Typography>
                  </Grid>
                  {studentSearch.selectedStudent.profile.phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                        {studentSearch.selectedStudent.profile.phone}
                      </Typography>
                    </Grid>
                  )}
                  {studentSearch.selectedStudent.profile.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {studentSearch.selectedStudent.profile.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Appointments */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Appointments ({studentSearch.selectedStudent.appointment_count})
                </Typography>
                
                {studentSearch.selectedStudent.appointments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No appointments found for this student.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Location</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentSearch.selectedStudent.appointments.map((appointment, index) => {
                          // Debug logging to see the appointment structure
                          console.log('Appointment data:', appointment);
                          
                          const appointmentDate = appointment.date ? new Date(appointment.date) : new Date();
                          const appointmentId = appointment.id || appointment.appointmentId || null;
                          
                          // Handle status - Acuity appointments might not have a direct status field
                          let status: { text: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' };
                          if (appointment.canceled) {
                            status = { text: 'Canceled', color: 'error' };
                          } else if (appointment.confirmed) {
                            status = { text: 'Confirmed', color: 'success' };
                          } else {
                            status = { text: 'Scheduled', color: 'primary' };
                          }
                          
                          // Fallback if no status was set
                          if (!status) {
                            status = { text: 'Unknown', color: 'default' };
                          }
                          
                          return (
                            <TableRow 
                              key={appointmentId || index}
                              hover
                              onClick={() => {
                                console.log('Clicking appointment with ID:', appointmentId);
                                if (appointmentId) {
                                  handleAppointmentClick(appointmentId);
                                } else {
                                  console.error('No appointment ID found for appointment:', appointment);
                                }
                              }}
                              sx={{ 
                                cursor: appointmentId ? 'pointer' : 'default',
                                '&:hover': appointmentId ? {
                                  backgroundColor: 'action.hover',
                                } : {},
                              }}
                            >
                              <TableCell>
                                {appointmentDate.toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {appointment.time || appointmentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </TableCell>
                              <TableCell>
                                {appointment.type || appointment.appointmentType || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={status.text} 
                                  color={status.color} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                {appointment.location || 'N/A'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseStudentModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Details Modal */}
      <Dialog 
        open={appointmentDetails.showModal} 
        onClose={handleCloseAppointmentModal}
        maxWidth="lg"
        fullWidth
        PaperProps={universalModalPaperProps}
        BackdropProps={universalModalBackdropProps}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EventIcon sx={{ mr: 1 }} />
              Appointment Details
            </Box>
            <IconButton onClick={handleCloseAppointmentModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {appointmentDetails.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading appointment details...
              </Typography>
            </Box>
          ) : appointmentDetails.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {appointmentDetails.error}
            </Alert>
          ) : appointmentDetails.selectedAppointment ? (
            <Box>
              {/* Basic Appointment Information */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Student Name
                    </Typography>
                    <Typography variant="body1">
                      {appointmentDetails.selectedAppointment['Student Name']}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Course Type
                    </Typography>
                    <Typography variant="body1">
                      {appointmentDetails.selectedAppointment['Course Type']}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Course Start Date
                    </Typography>
                    <Typography variant="body1">
                      {appointmentDetails.selectedAppointment['Course Start Date']}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Location
                    </Typography>
                    {/* Enhanced location display for courses with First Aid components */}
                    {appointmentDetails.selectedAppointment['Location'] === 'SOM' && 
                     appointmentDetails.selectedAppointment['Physical Location'] && 
                     appointmentDetails.selectedAppointment['Physical Location'] !== 'SOM' ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>First Aid Component:</strong> SOM
                        </Typography>
                        <Typography variant="body2">
                          <strong>Aquatic Components:</strong> {appointmentDetails.selectedAppointment['Physical Location']}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        {appointmentDetails.selectedAppointment['Location'] || 'N/A'}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="body1">
                      ${appointmentDetails.selectedAppointment.appointment_details.price}
                    </Typography>
                  </Grid>
                  {appointmentDetails.selectedAppointment['Add-Ons'] && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Add-Ons
                      </Typography>
                      <Typography variant="body1">
                        {appointmentDetails.selectedAppointment['Add-Ons']}
                      </Typography>
                    </Grid>
                  )}
                  {appointmentDetails.selectedAppointment.appointment_details.labels && appointmentDetails.selectedAppointment.appointment_details.labels.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Labels
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {appointmentDetails.selectedAppointment.appointment_details.labels.map((label, index) => {
                          // Handle labels that might be objects or strings
                          const labelText = typeof label === 'object' ? (label.name || label.label || JSON.stringify(label)) : String(label);
                          const labelColor = typeof label === 'object' && label.color ? label.color : undefined;
                          
                          return (
                            <Chip 
                              key={index} 
                              label={labelText} 
                              size="small" 
                              variant="outlined"
                              sx={labelColor ? { borderColor: labelColor, color: labelColor } : {}}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Student Information */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Student Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                      {appointmentDetails.selectedAppointment['Email']}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date of Birth
                    </Typography>
                                          <Typography variant="body1">
                        {appointmentDetails.selectedAppointment['Date of Birth'] || 
                         (appointmentDetails.selectedAppointment.structured_form_data ? 
                         Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('date of birth'))
                         ) ? Object.entries(Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('date of birth'))
                         ) || {}).find(([key]) => key.toLowerCase().includes('date of birth'))?.[1] || 'N/A' : 'N/A' : 'N/A')}
                      </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      LSS ID
                    </Typography>
                                          <Typography variant="body1">
                        {appointmentDetails.selectedAppointment['LSS ID'] ||
                         (appointmentDetails.selectedAppointment.structured_form_data ?
                         Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('lss') && key.toLowerCase().includes('id'))
                         ) ? Object.entries(Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('lss') && key.toLowerCase().includes('id'))
                         ) || {}).find(([key]) => key.toLowerCase().includes('lss') && key.toLowerCase().includes('id'))?.[1] || 'N/A' : 'N/A' : 'N/A')}
                      </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Friend Group
                    </Typography>
                                          <Typography variant="body1">
                        {appointmentDetails.selectedAppointment['Friend Group'] ||
                         (appointmentDetails.selectedAppointment.structured_form_data ?
                         Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('group'))
                         ) ? Object.entries(Object.values(appointmentDetails.selectedAppointment.structured_form_data).find(form => 
                           Object.keys(form).some(key => key.toLowerCase().includes('group'))
                         ) || {}).find(([key]) => key.toLowerCase().includes('group'))?.[1] || 'N/A' : 'N/A' : 'N/A')}
                      </Typography>
                  </Grid>
                  {/* Home Address */}
                  {appointmentDetails.selectedAppointment.structured_form_data && 
                   Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                     Object.keys(form).some(key => key.toLowerCase().includes('address') || key.toLowerCase().includes('home'))
                   ) && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Home Address
                      </Typography>
                      <Typography variant="body1">
                        {appointmentDetails.selectedAppointment.structured_form_data ? 
                        Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                          Object.entries(form).find(([key]) => 
                            key.toLowerCase().includes('address') || (key.toLowerCase().includes('home') && !key.toLowerCase().includes('phone'))
                          )?.[1]
                        ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                  {/* City */}
                  {appointmentDetails.selectedAppointment.structured_form_data && 
                   Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                     Object.keys(form).some(key => key.toLowerCase().includes('city'))
                   ) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        City
                      </Typography>
                      <Typography variant="body1">
                        {appointmentDetails.selectedAppointment.structured_form_data ?
                        Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                          Object.entries(form).find(([key]) => key.toLowerCase().includes('city'))?.[1]
                        ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                  {/* Postal Code */}
                  {appointmentDetails.selectedAppointment.structured_form_data && 
                   Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                     Object.keys(form).some(key => key.toLowerCase().includes('postal') || key.toLowerCase().includes('zip'))
                   ) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Postal Code
                      </Typography>
                      <Typography variant="body1">
                        {appointmentDetails.selectedAppointment.structured_form_data ?
                        Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                          Object.entries(form).find(([key]) => 
                            key.toLowerCase().includes('postal') || key.toLowerCase().includes('zip')
                          )?.[1]
                        ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Alternative Contact Information */}
              {appointmentDetails.selectedAppointment.structured_form_data && 
               Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                 Object.keys(form).some(key => 
                   key.toLowerCase().includes('alternate') || 
                   key.toLowerCase().includes('emergency')
                 )
               ) && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Alternative Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Alternate Email */}
                    {Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                      Object.keys(form).some(key => key.toLowerCase().includes('alternate') && key.toLowerCase().includes('email'))
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Alternate Email (If Applicable)
                        </Typography>
                        <Typography variant="body1">
                          {appointmentDetails.selectedAppointment.structured_form_data ?
                          Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                            Object.entries(form).find(([key]) => 
                              key.toLowerCase().includes('alternate') && key.toLowerCase().includes('email')
                            )?.[1]
                          ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                        </Typography>
                      </Grid>
                    )}
                    {/* Alternate Phone */}
                    {Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                      Object.keys(form).some(key => key.toLowerCase().includes('alternate') && key.toLowerCase().includes('phone'))
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Alternate Phone Number (If Applicable)
                        </Typography>
                        <Typography variant="body1">
                          {appointmentDetails.selectedAppointment.structured_form_data ?
                          Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                            Object.entries(form).find(([key]) => 
                              key.toLowerCase().includes('alternate') && key.toLowerCase().includes('phone')
                            )?.[1]
                          ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                        </Typography>
                      </Grid>
                    )}
                    {/* Emergency Contact */}
                    {Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                      Object.keys(form).some(key => key.toLowerCase().includes('emergency'))
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Emergency Contact Phone Number
                        </Typography>
                        <Typography variant="body1">
                          {appointmentDetails.selectedAppointment.structured_form_data ?
                          Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                            Object.entries(form).find(([key]) => 
                              key.toLowerCase().includes('emergency') && key.toLowerCase().includes('phone')
                            )?.[1]
                          ).filter(Boolean)[0] || Object.values(appointmentDetails.selectedAppointment.structured_form_data).map(form => 
                            Object.entries(form).find(([key]) => key.toLowerCase().includes('emergency'))?.[1]
                          ).filter(Boolean)[0] || 'N/A' : 'N/A'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}

              {/* First Aid Certification Upload */}
              {appointmentDetails.selectedAppointment.structured_form_data && 
               Object.values(appointmentDetails.selectedAppointment.structured_form_data).some(form => 
                 Object.keys(form).some(key => 
                   key.toLowerCase().includes('first aid') && 
                   (key.toLowerCase().includes('upload') || key.toLowerCase().includes('picture') || key.toLowerCase().includes('certification'))
                 )
               ) && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    First Aid Certification Documentation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    If your First Aid certification is from any organizations other than Lifesaving Society, please upload a picture of it here or email us the picture prior to your course.
                  </Typography>
                  <Grid container spacing={2}>
                    {appointmentDetails.selectedAppointment.structured_form_data ?
                    Object.values(appointmentDetails.selectedAppointment.structured_form_data).map((form, formIndex) => 
                      Object.entries(form).filter(([key]) => 
                        key.toLowerCase().includes('first aid') && 
                        (key.toLowerCase().includes('upload') || key.toLowerCase().includes('picture') || key.toLowerCase().includes('certification'))
                      ).map(([fieldName, fieldValue], index) => (
                        <Grid item xs={12} key={`${formIndex}-${index}`}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {fieldName}
                          </Typography>
                          <Typography variant="body1">
                            {fieldValue || 'No file uploaded'}
                          </Typography>
                        </Grid>
                      ))
                    ) : null}
                  </Grid>
                </Paper>
              )}

              {/* Payment Information */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Chip 
                      label={appointmentDetails.selectedAppointment.appointment_details.paid === 'yes' ? 'Paid' : 'Unpaid'} 
                      color={appointmentDetails.selectedAppointment.appointment_details.paid === 'yes' ? 'success' : 'warning'} 
                      size="small" 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Amount Paid
                    </Typography>
                    <Typography variant="body1">
                      ${appointmentDetails.selectedAppointment.appointment_details.amount_paid || '0.00'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Course Schedule */}
              {appointmentDetails.selectedAppointment['Course Schedule'] && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Course Schedule
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      padding: 2,
                      borderRadius: 1
                    }}
                  >
                    {appointmentDetails.selectedAppointment['Course Schedule']}
                  </Typography>
                </Paper>
              )}



              {/* Administrative Details */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Administrative Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Appointment ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {appointmentDetails.selectedAppointment.appointment_details.appointment_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date Scheduled
                    </Typography>
                    <Typography variant="body2">
                      {appointmentDetails.selectedAppointment['Date Scheduled']}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Timezone
                    </Typography>
                    <Typography variant="body2">
                      {appointmentDetails.selectedAppointment.appointment_details.timezone || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Notes */}
              {appointmentDetails.selectedAppointment.appointment_details.notes && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      padding: 2,
                      borderRadius: 1
                    }}
                  >
                    {appointmentDetails.selectedAppointment.appointment_details.notes}
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : null}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseAppointmentModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Attendance; 